import React, { useEffect, useState } from 'react';
import {
    BunkerFormHeader,
    BunkerRegFormWrapper,
    BunkeringStatusButton,
    BunkeringStatusChangeOptionsWrapper,
    BunkerRegColumnWrapper,
} from '../Bunkering.styles';
import { useBunkerContext } from 'src/context/BunkerContext';
import OdfjellPanel from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import useGetBunkeringData from 'src/api/bunkering/useGetBunkeringData';
import { DateTimeFormat, formatDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';
import { mapBunkering } from 'src/factory/Bunkering/BunkeringFactory';
import OdfjellInputComponent from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { MainButton } from 'src/styles/button.style';

interface DeletedBunkeringDetailsProps {
    deletedBunkeringId: string;
    onClose: () => void;
}

const DeletedBunkeringDetails: React.FC<DeletedBunkeringDetailsProps> = ({ deletedBunkeringId, onClose }) => {
    // Always define all hooks at the top level
    const { deletedBunkerings } = useBunkerContext();
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState<boolean>(false);
    const { upsertBunkering } = useGetBunkeringData();

    // Find the deleted bunkering
    const deletedBunkering = deletedBunkerings.find((_bunkering) => _bunkering.bunkeringGuid === deletedBunkeringId);

    useEffect(() => {
        if (!deletedBunkering) {
            setIsConfirmDeleteOpen(false);
            onClose();
        }
    }, [deletedBunkering, onClose]);

    const handleDeleteConfirmation = async () => {
        try {
            if (deletedBunkering) {
                deletedBunkering.deleted = true;
                const _bunkeringModel = mapBunkering(deletedBunkering);
                await upsertBunkering(deletedBunkering.imoNumber, _bunkeringModel);
            }
        } catch (error) {
            console.error('Error permanently deleting bunkering:', error);
        }
    };

    // Render different content based on whether bunkering is found or not
    if (!deletedBunkering) {
        return (
            <BunkerRegFormWrapper>
                <BunkerFormHeader>
                    <h2>Deleted Bunkering Not Found</h2>
                </BunkerFormHeader>
                <BunkerRegColumnWrapper>
                    <p>The deleted bunkering record could not be found.</p>
                    <BunkeringStatusChangeOptionsWrapper>
                        <BunkeringStatusButton onClick={onClose}>Close</BunkeringStatusButton>
                    </BunkeringStatusChangeOptionsWrapper>
                </BunkerRegColumnWrapper>
            </BunkerRegFormWrapper>
        );
    }

    return (
        <BunkerRegFormWrapper>
            <BunkerFormHeader>
                <h2>Deleted Bunkering Details</h2>
            </BunkerFormHeader>

            <PortLogDeletedWarning>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                The port log event corresponding to this bunkering registration has been deleted. Please delete this registration.
            </PortLogDeletedWarning>

            <BunkerRegColumnWrapper>
                <OdfjellInputComponent type="text" description="Bunkering Reference" value={deletedBunkering.bunkeringReference} isReadOnly={true} />
                <OdfjellInputComponent type="text" description="Fuel Type" value={FuelType[deletedBunkering.fuelType]} isReadOnly={true} />
                <OdfjellInputComponent
                    type="text"
                    description="Bunkering Date / Time"
                    value={formatDateTime(deletedBunkering.bunkeringDateTime, DateTimeFormat.prettifyFormats.dateTime)}
                    isReadOnly={true}
                />

                <OdfjellInputComponent
                    type="text"
                    description="Last updated"
                    value={formatDateTime(deletedBunkering.lastUpdatedAt, DateTimeFormat.prettifyFormats.dateTime)}
                    isReadOnly={true}
                />

                <OdfjellInputComponent
                    type="text"
                    description="Last updated by"
                    value={deletedBunkering.lastUpdatedBy || 'Unknown'}
                    isReadOnly={true}
                />

                <BunkeringStatusChangeOptionsWrapper>
                    <DeleteButton onClick={() => setIsConfirmDeleteOpen(true)}>
                        <FontAwesomeIcon icon={faTrashAlt} /> Delete Registration
                    </DeleteButton>
                </BunkeringStatusChangeOptionsWrapper>
            </BunkerRegColumnWrapper>

            {isConfirmDeleteOpen && (
                <OdfjellPanel
                    headerTitle="Confirm Deletion"
                    panelWidth={500}
                    defaultPosition="center"
                    hasCurtain={true}
                    onCancel={() => setIsConfirmDeleteOpen(false)}
                    onAction={handleDeleteConfirmation}
                    actionButtonText="Delete"
                    showCancelButton={true}
                    cancelButtonText="Cancel"
                >
                    <ConfirmationContent>
                        <p>Are you sure you want to delete this bunkering registration?</p>
                        <p>Bunkering Reference: {deletedBunkering.bunkeringReference}</p>
                        <p>Bunkering Date / time : {formatDateTime(deletedBunkering.bunkeringDateTime, DateTimeFormat.prettifyFormats.dateTime)}</p>
                        <p>Fuel Type: {FuelType[deletedBunkering.fuelType]}</p>
                    </ConfirmationContent>
                </OdfjellPanel>
            )}
        </BunkerRegFormWrapper>
    );
};

// Keeping this custom style as there isn't a direct equivalent in Bunkering.styles.ts
const PortLogDeletedWarning = styled.div`
    background-color: #fff3cd;
    color: #856404;
    border-left: 4px solid #ffc107;
    padding: 16px;
    margin: 0 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 500;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const DeleteButton = styled(MainButton)`
    background-color: #d9534f;
    color: white;
    border: 1px solid #d43f3a;
    font-size: 14px;

    &:hover {
        background-color: #c9302c;
        border-color: #ac2925;
    }

    &:focus {
        box-shadow: 0 0 0 0.2rem rgba(217, 83, 79, 0.5);
    }
`;

const ConfirmationContent = styled.div`
    padding: 16px;

    p {
        margin-bottom: 16px;
    }

    strong {
        color: #f44336;
    }
`;

export default DeletedBunkeringDetails;
